import { useCallback, useEffect, useState } from 'react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { GetHydratedCallRecordRequest } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { CallIntelTabType } from '@frontend/call-intel-core';
import { useIsInsideDemoPanel, useDemoPanelStore } from '@frontend/demo-panels';
import { useTranslation } from '@frontend/i18n';
import { useForceTray, useGlobalPanel, WIDTH_DIMS } from '@frontend/panel-engine';
import { useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { CallInsightAccordionType, CallInsightPanelSource, PanelEvents, ScreenType } from '../types';
import { defaultPanelContext, useCallInsightPanelShallowStore } from './use-call-insight-panel-store';
import { usePanelEmitter } from './use-panel-emitter';

function registerDemoCallInsightPanel() {
  // Dynamic import is intentional: a static import of '../components/call-insight-panel-demo-inline' would create a
  // circular module dependency (this hook → panel → call-insight-screen → this hook), causing
  // a "Cannot access before initialization" error in cypress tests. The deferred import() breaks the cycle at
  // module evaluation time without requiring React.lazy or Suspense.
  import('../components/call-insight-panel-demo-inline').then(({ CallInsightPanelDemoInline }) => {
    useDemoPanelStore.getState().register({
      id: 'demo-call-insight',
      component: CallInsightPanelDemoInline,
      width: WIDTH_DIMS['medium'],
    });
  });
}

export type OpenParams =
  | {
      // Mode A: Already have a full HydratedCallRecord
      hydratedCall: HydratedCallRecord;
    }
  | {
      // Mode B1: callRecordId with includeCallIntelligence defaulting to true
      callRecordId: string;
      locationId: string;
      includeCallIntelligence?: true;
      callIntelData?: never;
    }
  | {
      // Mode B2: callRecordId with includeCallIntelligence = false, must pass callIntelData
      callRecordId: string;
      locationId: string;
      includeCallIntelligence: false;
      callIntelData: HydratedCallRecord['callIntelInsights'];
    }
  | {
      // Mode C1: channelId with includeCallIntelligence defaulting to true
      channelId: string;
      locationId: string;
      includeCallIntelligence?: true;
      callIntelData?: never;
    }
  | {
      // Mode C2: channelId with includeCallIntelligence = false, must pass callIntelData
      channelId: string;
      locationId: string;
      includeCallIntelligence: false;
      callIntelData: HydratedCallRecord['callIntelInsights'];
    };

type OptionParams = {
  defaultAccordion?: CallInsightAccordionType;
  defaultCallIntelTab?: CallIntelTabType;
  currentScreenView?: ScreenType;
};

export type OpenOptions = OptionParams & {
  suppressGlobalPanelOpen?: boolean;
  taskId?: string;
  autoplayRecording?: boolean;
};

type UseCallInsightControllerProps = {
  source: CallInsightPanelSource;
  view?: ScreenType;
};

type CallOpenRequest = {
  params: OpenParams;
  opts?: OpenOptions;
} | null;

type DirectHydratedCallResult = {
  isDirect: true;
  hydratedRecord: HydratedCallRecord;
};

type HydratedCallFetchRequest = {
  isDirect: false;
  request: GetHydratedCallRecordRequest;
  partialCallIntel?: HydratedCallRecord['callIntelInsights'];
  skipOriginalParams?: boolean;
};

type HydratedCallPreparationResult = DirectHydratedCallResult | HydratedCallFetchRequest;

const REQUIRED_CALL_INTEL_KEYS = [
  'contactType',
  'callSentiment',
  'categories',
  'appointmentTypes',
  'serviceQualityFlags',
] as const;

// Helper Functions

const hasMissingCallIntelligenceKeys = (hydratedCall?: HydratedCallRecord) => {
  const insights = hydratedCall?.callIntelInsights;
  if (!insights) return false;
  return REQUIRED_CALL_INTEL_KEYS.some((key) => !Object.prototype.hasOwnProperty.call(insights, key));
};

/**
 * Prepares the hydration request or returns a direct record if already available.
 */

const prepareHydratedCall = (params: OpenParams): HydratedCallPreparationResult => {
  if ('hydratedCall' in params) {
    if (params.hydratedCall && hasMissingCallIntelligenceKeys(params.hydratedCall)) {
      return {
        isDirect: false,
        request: {
          locationId: params.hydratedCall.locationId ?? '',
          channelId: params.hydratedCall.channelId ?? params.hydratedCall.callIntelInsights?.id,
          includeCallRecording: true,
          hydrationFlags: {
            includeCallRecording: true,
            includeCallIntelligence: true,
            includeUserInfo: true,
            includePersonInfo: true,
            includePhoneTreeData: true,
            includeDownloadCallRecordingToken: true,
          },
        },
        skipOriginalParams: true,
      };
    }
    return {
      isDirect: true,
      hydratedRecord: params.hydratedCall,
    };
  }

  const isChannel = 'channelId' in params;
  const id = isChannel ? params.channelId : params.callRecordId;
  const locationId = params.locationId;
  const includeIntelligence = params.includeCallIntelligence !== false;

  const request: GetHydratedCallRecordRequest = {
    locationId,
    includeCallRecording: true,
    hydrationFlags: {},
  };

  if (isChannel) {
    request.channelId = id;
  } else {
    request.callRecordId = id;
  }

  if (includeIntelligence) {
    request.hydrationFlags = {
      includeCallRecording: true,
      includeCallIntelligence: true,
      includeUserInfo: true,
      includePersonInfo: true,
      includePhoneTreeData: true,
      includeDownloadCallRecordingToken: true,
    };
    return {
      isDirect: false,
      request,
    };
  }

  const partialCallIntel = params.callIntelData;
  const needUserInfo = !(partialCallIntel && partialCallIntel.officeUser);
  const needPersonInfo = !(partialCallIntel && partialCallIntel.person);

  request.hydrationFlags = {
    includeCallRecording: true,
    includeCallIntelligence: false,
    includeUserInfo: needUserInfo,
    includePersonInfo: needPersonInfo,
    includePhoneTreeData: true,
    includeDownloadCallRecordingToken: true,
  };

  return {
    isDirect: false,
    request,
    partialCallIntel,
  };
};

/**
 * Merges server and partial call intelligence data, syncing top-level and nested fields.
 */
const mergeHydratedCallFields = (
  serverRecord: HydratedCallRecord,
  partialCallIntel?: HydratedCallRecord['callIntelInsights']
): HydratedCallRecord => {
  const mergedRecord: HydratedCallRecord = { ...serverRecord };

  let baseCallIntel: HydratedCallRecord['callIntelInsights'] | undefined = partialCallIntel;
  if (!baseCallIntel && serverRecord.callIntelInsights) {
    baseCallIntel = serverRecord.callIntelInsights;
  }

  if (!baseCallIntel) {
    return mergedRecord;
  }

  // Upward sync: fill top-level fields from nested if missing
  if ((!mergedRecord.person || !mergedRecord.person.personId) && baseCallIntel.person) {
    mergedRecord.person = { ...baseCallIntel.person, personId: baseCallIntel.person.id };
  }
  if ((!mergedRecord.user || !mergedRecord.user.userId) && baseCallIntel.officeUser) {
    mergedRecord.user = baseCallIntel.officeUser;
  }

  const personData: CallIntelTypes.CallRecord['person'] = {
    ...baseCallIntel.person,
    firstName: baseCallIntel.person?.firstName ?? mergedRecord.person?.firstName,
    lastName: baseCallIntel.person?.lastName ?? mergedRecord.person?.lastName,
    preferredName: baseCallIntel.person?.preferredName ?? mergedRecord.person?.preferredName,
    id: baseCallIntel.person?.id ?? mergedRecord.person?.personId,
  };

  // Downward sync: fill nested fields from top-level if missing
  mergedRecord.callIntelInsights = {
    ...baseCallIntel,
    person: personData,
    officeUser: baseCallIntel.officeUser ?? mergedRecord.user,
  };

  return mergedRecord;
};

export interface CallInsightApi {
  activeSource: CallInsightPanelSource | null;
  activeCall: HydratedCallRecord | null;
  isLoading: boolean;
  isOpen: boolean;
  emitEvent: <K extends keyof PanelEvents>(event: K, payload?: PanelEvents[K]) => void;
  goToNextCall: () => void;
  goToPreviousCall: () => void;
  open: (params: OpenParams, opts?: OpenOptions) => void;
  offEvent: <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => void;
  onEvent: <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => void;
  /**
   * Supply the full list (in the same OpenParams shape)
   * to enable navigation.
   */
  setCallList: (callListItems: OpenParams[], callListOptions?: OpenOptions) => void;
  setIsDemoAccount: (isDemoAccount: boolean) => void;
  updateCallInsights: (callIntel: HydratedCallRecord['callIntelInsights']) => void;
  clearCallList: () => void;
}

export const useCallInsightController = ({ source, view }: UseCallInsightControllerProps): CallInsightApi => {
  const alert = useAlert();
  const { t } = useTranslation('call-insight-panel');

  const { open: openGlobal, isOpen } = useGlobalPanel('call-insight');
  const { enableForceTray } = useForceTray();

  const [callOpenRequest, setCallOpenRequest] = useState<CallOpenRequest>(null);

  const emitter = usePanelEmitter(source);

  const {
    activeSource,
    contexts,
    openContext,
    setCallListItems: dispatchCallListItems,
    setIsDemoAccount: setIsDemoAccountForSource,
  } = useCallInsightPanelShallowStore(
    'activeSource',
    'contexts',
    'openContext',
    'setCallListItems',
    'setIsDemoAccount'
  );

  const activeCall = activeSource && contexts[activeSource] ? contexts[activeSource].selectedCall : null;

  const isDemoAccount = !!(source && contexts[source]?.isDemoAccount);
  const ctx = contexts[source] ?? defaultPanelContext;
  const { callListOptions, nextCallItem, previousCallItem } = ctx;

  const { data: fetchedCallResponse, isLoading: isHydratedCallLoading } = useScopedQuery({
    queryKey: ['hydrated-call-record', JSON.stringify(callOpenRequest?.params)],
    queryFn: async () => {
      if (!callOpenRequest) throw new Error('No callOpenRequest provided');
      const { request } = callOpenRequest.params as any as { request: GetHydratedCallRecordRequest };
      return PhoneCallsApi.getCallRecord({
        ...request,
        isDemo: isDemoAccount,
        demoMetadata:
          isDemoAccount && 'callIntelData' in callOpenRequest.params && callOpenRequest.params.callIntelData
            ? ({
                personId: callOpenRequest.params.callIntelData?.person?.id,
                callRecord: JSON.stringify(callOpenRequest.params.callIntelData),
              } as any) // Some of these values are not in contract but will be used by demo interceptor
            : undefined,
      });
    },
    onError: () => {
      alert.error(t("Couldn't fetch call record data. Please try again."));
    },
    enabled:
      !!callOpenRequest &&
      !('hydratedCall' in callOpenRequest.params) &&
      !((prepareHydratedCall(callOpenRequest.params) as DirectHydratedCallResult).isDirect === true),
    cacheTime: 0,
  });

  const isInsideDemoPanel = useIsInsideDemoPanel();

  const openCallInsightPanel = useCallback(
    ({ forcedTray }: { forcedTray?: boolean }) => {
      if (isInsideDemoPanel) {
        registerDemoCallInsightPanel();
        return;
      }
      if (forcedTray) {
        enableForceTray();
      }
      openGlobal();
    },
    [enableForceTray, openGlobal, isInsideDemoPanel]
  );

  /**
   * Opens the call insight panel for a specific call or by id/channel.
   * - If hydratedCall is provided, open immediately.
   * - Otherwise, set callOpenRequest to trigger async fetch and open.
   */
  const open = useCallback(
    (params: OpenParams, opts?: OpenOptions) => {
      const hydrationPrep = prepareHydratedCall(params);
      const screenView = view ?? opts?.currentScreenView;
      const optsWithDefaults: OpenOptions = {
        ...opts,
        ...(screenView !== undefined ? { currentScreenView: screenView } : {}),
      };
      // Direct record: open immediately
      if (hydrationPrep.isDirect) {
        const mergedRecord = mergeHydratedCallFields(hydrationPrep.hydratedRecord);
        openContext({ call: mergedRecord, opts: optsWithDefaults, source });
        openCallInsightPanel({ forcedTray: opts?.suppressGlobalPanelOpen });
      } else {
        // Otherwise, stash request for the effect to pick up
        setCallOpenRequest({
          params: {
            ...(!hydrationPrep?.skipOriginalParams && { ...params }),
            request: (hydrationPrep as HydratedCallFetchRequest).request,
          } as any,
          opts,
        });
      }
    },
    [openContext, openCallInsightPanel, source]
  );

  /**
   * Navigate to the previous call in the navigation list.
   */
  const goToPreviousCall = useCallback(() => {
    if (!previousCallItem) return;
    open(previousCallItem, callListOptions);
  }, [previousCallItem, callListOptions, open]);

  /**
   * Navigate to the next call in the navigation list.
   */
  const goToNextCall = useCallback(() => {
    if (!nextCallItem) return;
    open(nextCallItem, callListOptions);
  }, [nextCallItem, callListOptions, open]);

  /**
   * Register an event handler for panel events.
   */
  const onEvent = useCallback(
    <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => {
      emitter.on(event, handler);
    },
    [emitter]
  );

  /**
   * Unregister an event handler for panel events.
   */
  const offEvent = useCallback(
    <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => {
      emitter.off(event, handler);
    },
    [emitter]
  );

  /**
   * Emit a panel event.
   */
  const emitEvent = useCallback(
    <K extends keyof PanelEvents>(event: K, payload?: PanelEvents[K]) => {
      emitter.emit(event, payload as PanelEvents[K]);
    },
    [emitter]
  );

  /**
   * Set the navigation items and options for call navigation.
   */
  const setCallListItems = useCallback(
    (callListItems: OpenParams[], callListOptions?: OpenOptions) => {
      dispatchCallListItems({ callListItems, callListOptions: callListOptions ?? {}, source });
    },
    [dispatchCallListItems, source]
  );

  /**
   * Cleanup: Exposed function to clear navigation data for this source.
   * Call this from the parent when you want to remove call list data for the current source.
   */
  const clearCallList = useCallback(() => {
    dispatchCallListItems({
      callListItems: [],
      callListOptions: {},
      source,
    });
  }, [dispatchCallListItems, source]);

  /**
   * Set whether the current source is a demo account.
   */
  const setIsDemoAccount = useCallback(
    (isDemoAccount: boolean) => setIsDemoAccountForSource({ isDemoAccount, source }),
    [setIsDemoAccountForSource, source]
  );

  /**
   * Updates the currently open call in the Call Insight Panel with new call intelligence data.
   * If the panel is open and the provided call intelligence matches the active call,
   * merges the new data and updates the panel context.
   * @param callIntel - The new call intelligence data to merge into the active call.
   */
  const updateCallInsights = useCallback(
    (callIntel: HydratedCallRecord['callIntelInsights']) => {
      if (!activeCall || !callIntel || activeCall.channelId !== callIntel.id) return;
      const updated: HydratedCallRecord = mergeHydratedCallFields(activeCall, callIntel);
      if (hasMissingCallIntelligenceKeys(updated)) {
        const hydrationPrep = prepareHydratedCall({ hydratedCall: updated });
        setCallOpenRequest({
          params: {
            request: (hydrationPrep as HydratedCallFetchRequest).request,
          } as any,
        });
        return;
      } else {
        openContext({
          call: updated,
          source,
        });
      }
    },
    [activeCall, openContext, source]
  );

  /**
   * Effect: Handles opening logic for both direct and fetched records.
   * - If direct record, merge and open immediately.
   * - If fetch required, wait for fetch, then merge and open.
   */
  useEffect(() => {
    if (!callOpenRequest) return;

    const hydrationPrep = prepareHydratedCall(callOpenRequest.params);

    // Direct record: open immediately
    if (hydrationPrep.isDirect) {
      const mergedRecord = mergeHydratedCallFields(hydrationPrep.hydratedRecord);
      openContext({
        call: mergedRecord,
        opts: callOpenRequest.opts,
        source,
      });
      setCallOpenRequest(null);
      return;
    }

    // Wait for fetch if not direct
    if (!fetchedCallResponse?.record) {
      return;
    }

    // Merge server and partial call intelligence if present
    const partialCallIntel = (hydrationPrep as HydratedCallFetchRequest).partialCallIntel;
    const mergedRecord = mergeHydratedCallFields(fetchedCallResponse.record, partialCallIntel);

    openContext({
      call: mergedRecord,
      opts: callOpenRequest.opts,
      source,
    });
    openCallInsightPanel({ forcedTray: callOpenRequest.opts?.suppressGlobalPanelOpen });

    setCallOpenRequest(null);
  }, [callOpenRequest, fetchedCallResponse?.record, openContext, openGlobal, source, openCallInsightPanel]);

  const isLoading = !!(callOpenRequest && !('hydratedCall' in callOpenRequest.params) && isHydratedCallLoading);

  return {
    activeSource,
    activeCall,
    isLoading,
    isOpen,
    emitEvent,
    goToNextCall,
    goToPreviousCall,
    offEvent,
    open,
    onEvent,
    setCallList: setCallListItems,
    clearCallList,
    setIsDemoAccount,
    updateCallInsights,
  };
};
